import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from "@angular/common";
import { Router } from '@angular/router';
import { CoreLanguageService } from '../../services/language.service';
import { WebShopService } from '../../services/webShop.service';
import { Subscription } from 'rxjs';
import { CoreLogService } from '../../services/log.service';
import { ChangeDetectionStrategy } from '@angular/compiler/src/core';
import { RemoteGroupOrderService } from '../../services/RemoteGroupOrder.service';
import { IGroupSummary } from './remoteOrderGroupSummary.facade';
import { RemoteOrderGroupSummaryService } from './remoteOrderGroupSummary.service';
import { WebShopMode } from "../../services/IWebShopService";
import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { ILocationData, Status } from '../../services/IRemoteGroupOrderService';

@Component({
  selector: 'remoteOrderGroupSummary',
  templateUrl: './remoteOrderGroupSummary.component.html',
  styleUrls: ['./remoteOrderGroupSummary.component.scss'],
  providers: [RemoteOrderGroupSummaryService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("serviceButtonEntering", [
      transition("false => true", [
        query(".animate", [
          style({ transform: "scale(0)" }),
          stagger(100, [
            animate("400ms ease-out", style({ transform: "scale(1.1)" })),
            animate("100ms ease-in-out", style({ transform: "scale(1)" }))
          ])
        ])
      ])
    ]),
  ]
})

export class RemoteOrderGroupSummaryComponent implements OnInit, OnDestroy {
  private _langSubscriber: Subscription;
  private _serviceTimeExpiredSubscription!: Subscription;

  public isLoaded: boolean = false;
  public useBrowserHistory: boolean = true;
  public isSmallView: boolean = false;

  public groupSummary: IGroupSummary | null = null;
  public locationData: ILocationData | null = null;
  public groupStatus = Status;

  constructor(private _router: Router
    , private _changeDetectorRef: ChangeDetectorRef
    , private _coreLogService: CoreLogService
    , private _location: Location
    , private _webShopService: WebShopService
    , private _remoteGroupOrderService: RemoteGroupOrderService
    , private _remoteOrderGroupSummaryService: RemoteOrderGroupSummaryService
    , private _coreLanguageService: CoreLanguageService
  ) { }

  ngOnDestroy(): void {
    const me = this;
    if (me._langSubscriber) {
      me._langSubscriber.unsubscribe();
    }
    if (this._serviceTimeExpiredSubscription) {
      this._serviceTimeExpiredSubscription.unsubscribe();
    }
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("RemoteOrderGroupSummaryComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngOnInit() {
    const me = this;
    this._remoteOrderGroupSummaryService.loadGroupSummary().then((summary) => {
      if (!summary) return; // exit early if no summary is found
      // Update service time expiration status
      this._remoteOrderGroupSummaryService.updateServiceTimeStatus(summary.serviceTime);

      // Subscribe correctly (storing reference for cleanup)
      this._serviceTimeExpiredSubscription = this._remoteOrderGroupSummaryService.ServiceTimeExpiredSubject.subscribe((expired) => {
        if (expired) {
          this._coreLogService.info("RemoteOrderGroupSummaryComponent", "Service time expired, navigating to start page.");
          this.navigateToHome();
        }
      });

      // Load location data if available
      if (summary.locationID) {
        this.loadLocationData(summary.locationID);
      }
      this.groupSummary = summary;
      this.isLoaded = true;
      this.PushChanges();
    }).catch(error => {
      this._coreLogService.error("Error loading group summary:", error);
    });

  }

  private loadLocationData(locationid: number) {
    this._remoteOrderGroupSummaryService.loadLocationData(locationid).then((locationData) => {
      if (locationData) {
        this.locationData = locationData;
        this.PushChanges();
      }
    })
  }

  public ServiceTimeExpired(serviceTime: string): boolean {
    const selectedServiceTime = serviceTime ? new Date(serviceTime) : null;
    const currentTime = new Date();
    return !!selectedServiceTime && selectedServiceTime < currentTime;
  }

  public isOrderDelivery(ordermode: number) {
    return ordermode === WebShopMode.DeliveryGroup;
  }

  public stateFinished(state: number) {
    return (state === this.groupStatus.Finished);
  }

  /** Formats the given amount into a currency string.*/
  public formatCurrency(amount: number): string {
    const me = this;
    return me._webShopService.formatCurrency(amount);
  }

  /** Retrieves the group name text with the given group name.*/
  public GetGroupNameText(groupName: string): string {
    const me = this;
    return me._coreLanguageService.value("phRemoteOrder_Group_Summary_Title")
      .replace("{groupname}", "\n" + groupName);
  }

  /** Sets the delivery end time with the given service time.*/
  public setDeliveryEndTime(selectedServiceTime: string) {
    const me = this;
    const day = me._displayDay(selectedServiceTime);
    const time = me._displayTime(selectedServiceTime);
    const isdayToday = me._isDayToday(selectedServiceTime);
    if (isdayToday) {
      return me._coreLanguageService.value("phRemoteOrder_Group_Summary_ServiceTime_Today_Delivery")
        .replace("{time}", `<span class="emphasized">${time}</span>`);
    } else {
      return me._coreLanguageService.value(" phRemoteOrder_Group_Summary_ServiceTime_Delivery")
        .replace("{day}", `<span class="emphasized">${day}</span>`)
        .replace("{time}", `<span class="emphasized">${time}</span>`);
    }
  }

  /** Sets the pickup end time with the given service time. */
  public setPickupEndTime(selectedServiceTime: string) {
    const me = this;
    const day = me._displayDay(selectedServiceTime);
    const time = me._displayTime(selectedServiceTime);
    const isdayToday = me._isDayToday(selectedServiceTime);
    if (isdayToday) {
      return me._coreLanguageService.value("phRemoteOrder_Group_Summary_ServiceTime_Today_Pickup")
        .replace("{time}", `<span class="emphasized">${time}</span>`);
    } else {
      return me._coreLanguageService.value("phRemoteOrder_Group_Summary_ServiceTime_Pickup")
        .replace("{day}", `<span class="emphasized">${day}</span>`)
        .replace("{time}", `<span class="emphasized">${time}</span>`);
    }
  }

  /** Displays the time for the given selected time.*/
  private _displayTime(dateTime: string) {
    return this._remoteGroupOrderService.timeDisplayValue(dateTime);
  }

  /** Displays the day for the given selected time. */
  private _displayDay(selectedTime: string) {
    return this._remoteGroupOrderService.dayDisplayValue(selectedTime)
  }

  /**  Checks if the given day is today */
  private _isDayToday(selectedDay: string) {
    return this._remoteGroupOrderService.isDayToday(selectedDay)
  }


  /**  Sets the delivery group address with the given address.*/
  public setDeliveryGroupAddress(address: string) {
    const me = this;
    return me._coreLanguageService.value("phRemoteOrder_Group_Summary_Delivery_Address_To")
      .replace("{groupaddress}", `<span class="emphasized">${address}</span>`)
  }

  public setDeliveryLocationName(name: string) {
    const me = this;
    return me._coreLanguageService.value("phRemoteOrder_Group_Summary_Delivery_From")
      .replace("{locationname}", `<span class="emphasized">${name}</span>`)
  }

  public navigateToHome() {
    const me = this;
    const expired = me._remoteGroupOrderService.ServiceTimeExpired();
    if (expired) {
      me._router.navigate(["/"]);
    }
  }

  /**If small view,navigate back by swiping from left to right.*/
  public swiperNavigateBack() {
    const me = this;
    if (me.isSmallView == true) {
      me.navigateBack();
    }
  }

  /** Navigate back*/
  public navigateBack() {
    const me = this;
    if (me.useBrowserHistory) {
      me._location.back();
    } else {
      me._router.navigate(["/"], { replaceUrl: true });
    }
  }

}